import React, { forwardRef, useState } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';

import APFormGroup from 'components/FormFields/APFormGroup';
import APModal from 'components/Modal/APModal';
import logo from 'assets/images/ap_logo_final_RGB_800px.png';
import { STATES } from 'utils/constants';
import { convertCurrency } from 'utils/general';
import { formatDate } from '../index';

export const BillingSummary = forwardRef((props, ref) => {
  const {
    selectedPlan,
    planPrice,
    billingCycle,
    subscription,
    invoice,
    plans,
    creditApplied,
    redeemedPromoCode,
    isTrial,
    isPromo,
    // isExpired,
    expiresAt,
    remainingTrialDays,
    isPlanChange,
    isIntervalChange,
    isUpgrade,
    isCancel,
    isDelinquent,
    onSubscribe,
    onClear,
    subscriptionUpdating,
  } = props;
  const [changePlanModalOpened, setChangePlanModalOpened] = useState(false);
  const [taxDisclaimerModalOpened, setTaxDisclaimerModalOpened] = useState(false);

  const {
    startingBalance = 0,
    subtotal = 0,
    amountDue,
    customerAddressState,
    lines = [],
    discount,
    discountCode,
    periodStart,
    periodEnd,
    notDiscountable,
  } = invoice || {};

  const { name, renew } = selectedPlan || {};
  const { start, end } = billingCycle || {};

  // If invoice is not created but user has a discount code, we need to calculate discount
  // to add to the billing summary manually.
  const amountOff = selectedPlan ? selectedPlan.price - selectedPlan.discountedPrice :  0;
  // notDiscountable is a flag from the api to not use discount in the billing summary.
  // This is for accounts that have a coupon code when first subscribing and are trying to subscribe
  // to a yearly plan.
  const showDiscount = !notDiscountable && (amountOff > 0 || discount > 0);

  // Invoice lines are always to be assumed to be proration calculations.
  const invoiceLines = lines.filter(line => line.type === 'invoiceitem');
  // This assumes there will only ever be 1 subscription line in a stripe invoice.
  // If this ever changes, then the logic for this will need to change also.
  const invoiceSubscription = lines.find(line => line.type === 'subscription');
  // Additional subscription items that aren't the main subscription.
  const addtLines = lines.filter(line => line.type === 'subscription' && !isPlanCode(line.code, plans));

  // We have to calculate proration here because of how we fetch previews from stripe.
  // We include the next billing cycle invoice data so we don't have to calculate
  // possible discounts or other adjustments. Calculating proration is lower risk than
  // other adjustments.
  const prorationAmount = invoiceLines.reduce((acc, line) => acc + line.amount, 0);

  const chargeApplied = convertCurrency(startingBalance > 0 ? startingBalance : 0);
  const { tax = 0, taxPercent, date: invoiceDueDate } = invoice || {};
  const taxState = STATES.find(item => item.value === customerAddressState)?.label;

  const today = formatDate();
  const trialEnd = formatDate(expiresAt);

  // We don't want to show actions if the user is subscribed and selected the current plan so we prevent
  // them from trying to subscribe to the same plan. However, if an account is delinquent (payment failed)
  // Then they still show that they have a plan, but they need to be able to re-subscribe to the same plan.
  const showActions = isDelinquent || !subscription || isPlanChange || isCancel;

  // We only need to show plan change if user is currently subscribe and is previewing a different plan.
  const showPlanChange = isPlanChange && !isTrial && !isPromo;

  // If in trial, always show the trial end date.
  // Show "Due today" if user does not currently have a plan or is upgrading plan.
  // If not an interval change, only charge prorated amount immediately.
  const dueDateToday = () => {
    if (isTrial) return false;
    return (isPlanChange && isUpgrade) || !invoiceDueDate;
  };

  const calculateSubtotal = () => {
    // We ignore stripe subtotal when we are previewing a plan change, because we want
    // to display the plan price before proration as subtotal, whereas stripe calculates
    // subtotal with prorations. We also just display plan price if there is no invoice.
    if (!invoice || isPlanChange) return convertCurrency(planPrice);
    // Display current invoice subtotal.
    return convertCurrency(subtotal);
  };

  // #TODO: This function might not be needed anymore. I don't think we need to do
  // anything other than display proration amount here, but leaving it for now to avoid
  // unecessary refactoring.
  const calculatePlanChange = () => {
    // If changing interval, subtotal for invoice preview should be correct.
    if (isIntervalChange) {
      return convertCurrency(prorationAmount);
    }
    // Otherwise is just a price change and proration amount will be difference between
    // remaining time between both plans.
    return convertCurrency(prorationAmount);
  };

  const calculateTotal = () => {
    // Break out of logic if no amount is due.
    if (amountDue === 0) return convertCurrency(amountDue);

    // display the plan price if user doesn't have existing invoice.
    if (!invoice) {
      if (redeemedPromoCode && selectedPlan) return convertCurrency(selectedPlan?.discountedPrice || 0);
      return convertCurrency(planPrice);
    }

    // If same interval and the plan is changing, use proration amount in calculation
    // becuase of the way the invoice preview is fetched from the api. But we don't want to do this
    // if user is in trial since they haven't paid any money yet.
    if (!isTrial && (isPlanChange && !isIntervalChange)) {
      if (prorationAmount + startingBalance > 0) return convertCurrency(prorationAmount + startingBalance);
      // Don't need to show any amount is due if proration amount is negative.
      return convertCurrency(0);
    }

    if (notDiscountable) {
      return convertCurrency(amountDue + discount);
    }

    // Display the current invoice amount due.
    if (invoice) return convertCurrency(amountDue);

    // Should never occur, but is fallback.
    return convertCurrency(0);
  };

  const calculateDueDate = () => {
    // If user is in trial and no invoice, show trial end date
    if (isTrial) {
      if (trialEnd) {
        return formatDate(trialEnd) || today;
      }
      // If it's a yearly plan, pick the periodEnd date due to
      // how we get previews for yearly plans while in trial.
      // This is a weird edge case with how stripe generates previews
      // while subscription is in trial and we want to preview a yearly
      // plan while a montly plan is in trial.
      if (notDiscountable) {
        return formatDate(periodEnd);
      }

      // Special handling for annual plan preview while user is in trial.
      // For interval changes, we calculate the due date manually.
      if (isIntervalChange && remainingTrialDays != null && periodStart) {
        const annualTrialDueDate = dayjs(periodStart).add(remainingTrialDays, 'day');
        return formatDate(annualTrialDueDate);
      }
    }

    // If user is changing interval, charge immediately.
    if (isIntervalChange && isPlanChange) return today;

    // If there's an invoice, use the invoice date (this takes priority over trial status)
    if (invoice && invoiceDueDate) {
      const invoiceDate = formatDate(invoiceDueDate);
      return invoiceDate || today;
    }

    // If viewing a plan change that is not a change in interval, return start date of invoice preview.
    if (isPlanChange && invoice) {
      const planChangeDate = formatDate(invoiceDueDate);
      return planChangeDate || today;
    }

    // If user is in promo, show when the promo expires.
    if (isPromo) {
      const promoDate = formatDate(expiresAt);
      return promoDate || today;
    }

    // If not subscribed and not on trial, display today.
    return today;
  };

  return (
    <>
      <div ref={ref}>
        <APFormGroup
          title="Billing Summary"
          className="billing-summary"
          direction="vertical"
        >
          <div className="summary">
            <div className="summary__group">
              <div className="summary__row">
                <span>
                  PRO PLAN: <br /> {name}
                  {renew ? (
                    <span className="summary__period">
                      {renew === 'month' ? 'MONTHLY PAYMENT' : 'ANNUAL PAYMENT'}
                    </span>
                  ) : null}
                </span>
                <span>
                  {convertCurrency(planPrice)}
                </span>
              </div>
              {addtLines.map(({ description, amount }) => {
                return (
                  <div className="summary__row">
                    <span>
                      {description}
                    </span>
                    <span>
                      {convertCurrency(amount)}
                    </span>
                  </div>
                );
              })}
              {((start && end) || (periodStart && periodEnd)) && (
                <div className="plan-change__row">
                  {/* if the interval changes (e.g. monthly to yearly) we create a new billing cycle */}
                  {isIntervalChange ? (
                    <b>{formatDate(invoiceSubscription.start)} - {formatDate(invoiceSubscription.end)}</b>
                  ) : (
                    <b>{formatDate(periodStart)} - {formatDate(periodEnd)}</b>
                  )}
                </div>
              )}
            </div>

            <div className="summary__group">
              {(isTrial || !isPlanChange) && (
                <div className="summary__row">
                  <span>SUBTOTAL</span>
                  <span>
                    {calculateSubtotal()}
                  </span>
                </div>
              )}
              {showPlanChange && (
                <div className="summary__row">
                  <span>
                    PLAN CHANGE
                    <i
                      className="summary__info-icon"
                      onClick={() => setChangePlanModalOpened(true)}
                    >
                      i
                    </i>
                  </span>

                  <span>{calculatePlanChange()}</span>
                </div>
              )}
              {startingBalance < 0 ? (
                <div className="summary__row">
                  <span>CREDIT APPLIED</span>
                  <span>{creditApplied}</span>
                </div>
              ) : null}
              {startingBalance > 0 ? (
                <div className="summary__row">
                  <span>CHARGE APPLIED</span>
                  <span>{chargeApplied}</span>
                </div>
              ) : null}

              {showDiscount ? (
                <div className="summary__row">
                  <span>{discountCode || redeemedPromoCode}</span>
                  <span>
                    -{convertCurrency(discount || amountOff)}
                  </span>
                </div>
              ) : null}

              <div className="summary__row">
                <span>
                  TAX {(!!taxState || !!taxPercent) && '-'}
                  &nbsp;{!!taxState && taxState}
                  &nbsp;{(!!taxPercent && tax !== 0) && `${taxPercent}%`}
                  <i
                    className="summary__info-icon"
                    onClick={() => setTaxDisclaimerModalOpened(true)}
                  >
                    i
                  </i>
                </span>
                <span>{convertCurrency(tax)}</span>
              </div>
            </div>
            {selectedPlan ? (
              <div className="summary__group">
                <div className="summary__row">
                  {dueDateToday() ? (
                    <span>
                      TOTAL DUE TODAY
                    </span>
                  ) : (
                    <span>
                      TOTAL DUE ON <br />{calculateDueDate()}
                    </span>
                  )}
                  <span>
                    {calculateTotal()}
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          {showActions && (
            <div className="summary-buttons">
              <button
                className={clsx('button', {
                  disabled: subscriptionUpdating,
                })}
                onClick={onSubscribe}
              >
                {!subscriptionUpdating && (
                  isCancel || !invoice ? 'Subscribe' : 'Update Plan'
                )}
                {subscriptionUpdating && (
                  <>
                    <span className="visually-hidden--mobile">Subscribing</span>
                    <span className="fa fa-spin fa-spinner" />
                  </>
                )}
              </button>
              <button
                className="button button-grey"
                onClick={onClear}
              >
                Clear
              </button>
            </div>
          )}
        </APFormGroup>
      </div>
      {changePlanModalOpened && (
        <PlanChangeModal
          isIntervalChange={isIntervalChange}
          invoice={invoice}
          invoiceLines={invoiceLines}
          prorationAmount={prorationAmount}
          periodEnd={periodEnd}
          setChangePlanModalOpened={setChangePlanModalOpened}
        />
      )}
      {taxDisclaimerModalOpened && (
        <SalesTaxModal
          setTaxDisclaimerModalOpened={setTaxDisclaimerModalOpened}
        />
      )}
    </>
  );
});

const PlanChangeModal = ({
  isIntervalChange,
  invoice,
  invoiceLines,
  prorationAmount,
  periodEnd,
  setChangePlanModalOpened,
}) => {
  // If there's are no invoice line items, there are no prorations happening.
  if (!invoice || invoiceLines.length < 1) return;

  // If isIntervalChange, assume there's only 1 invoice line which is unused time on current plan.
  const { end: invoiceItemEnd } = invoiceLines[0];

  return (
    <APModal
      open
      onClose={() => setChangePlanModalOpened(false)}
      actions={(
        <button
          onClick={() => setChangePlanModalOpened(false)}
          className="button"
        >
          Close
        </button>
      )}
    >
      <div className="body-content container--small">
        <h3 className="invoice-modal__title">
          <img
            src={logo}
            alt="Aisle Planner"
            className="ap-logo"
          />
        </h3>
      </div>
      <div className="plan-change">
        <div className="plan-change__row">
          <b>
            Prorated amount calculation
            ({formatDate()} - {formatDate(isIntervalChange ? invoiceItemEnd : periodEnd)})
          </b>
        </div>
        {invoiceLines.map((line, idx) => {
          return (
            <div
              key={`invoice-line-${idx}`}
              className="plan-change__row"
            >
              <span>{line.description}</span>
              <b>{convertCurrency(line.amount)}</b>
            </div>
          );
        })}
        <hr />
        <div className="plan-change__row">
          <b>Plan change prorated amount</b>
          <b>{convertCurrency(prorationAmount)}</b>
        </div>
      </div>
    </APModal>
  );
};

const SalesTaxModal = ({ setTaxDisclaimerModalOpened }) => {
  return (
    <APModal
      open
      name="tax-disclaimer"
      onClose={() => setTaxDisclaimerModalOpened(false)}
      actions={(
        <button
          onClick={() => setTaxDisclaimerModalOpened(false)}
          className="button"
        >
          Close
        </button>
      )}
    >
      <p>
        Applicable sales tax for US businesses may apply and will be calculated based on your billing
        location after checkout.
      </p>
      <p>
        For new subscriptions and change of billing location, your total due may vary from what is
        currently shown below to reflect any applicable sales tax.
      </p>
    </APModal>
  );
};

export const isPlanCode = (code, plans) => {
  const codes = [];
  Object.keys(plans).forEach(planType => {
    if (Array.isArray(plans[planType])) {
      plans[planType].forEach(plan => {
        if (plan.code) {
          codes.push(plan.code);
        }
      });
    }
  });

  return codes.includes(code);
};
